import { useState, useEffect, useCallback, useRef } from "react";
import { WifiOff } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const PROBE_URL = `${(import.meta.env.BASE_URL || "/").replace(/\/$/, "")}/api/auth/me`;
const PROBE_INTERVAL_MS = 15_000;

export function NetworkStatusBanner() {
  const { t } = useTranslation("common");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [fetchFailed, setFetchFailed] = useState(false);
  const probeTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setFetchFailed(false);
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const probe = useCallback(async () => {
    try {
      await fetch(PROBE_URL, { credentials: "include", cache: "no-store" });
      if (fetchFailed) {
        setFetchFailed(false);
        queryClient.invalidateQueries();
      }
    } catch {
      setFetchFailed(true);
    }
  }, [fetchFailed, queryClient]);

  useEffect(() => {
    if (isOffline || fetchFailed) {
      probeTimer.current = setInterval(probe, PROBE_INTERVAL_MS);
      return () => {
        if (probeTimer.current) clearInterval(probeTimer.current);
      };
    }
    return undefined;
  }, [isOffline, fetchFailed, probe]);

  useEffect(() => {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await origFetch(...args);
        return response;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          throw err;
        }
        if (err instanceof TypeError) {
          setFetchFailed(true);
        }
        throw err;
      }
    };
    return () => {
      window.fetch = origFetch;
    };
  }, []);

  const showBanner = isOffline || fetchFailed;
  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500/95 text-black px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 shadow-lg">
      <WifiOff className="w-4 h-4" />
      {t("connectionLost")}
    </div>
  );
}
